import { PropsWithChildren, ReactElement, useMemo } from "react";

import { HStack } from "@chakra-ui/react";

import { useLocation } from "react-router-dom";

import { Entity } from "@/jenesys-types/data/common";
import { DataType } from "@/jenesys-types/data/enums";

import { NamedLink } from "@/components/common/navigation/NamedLink";
import { getIdParamName } from "@/routes/crud";
import { Accessor, getAccessorValue } from "@/util/data";
import { generateTestId } from "@/util/testIds";

import { EntityDeleteButton } from "./EntityDeleteButton";
import { EntityEditButton } from "./EntityEditButton";

export interface EntityCrudActionsProps<E extends Entity> {
  addReturnPath?: boolean;
  canDelete?: Accessor<E, boolean>;
  canEdit?: Accessor<E, boolean>;
  dataType: DataType;
  editQueryParams?: Record<string, any>;
  editRouteName?: string;
  item: E;
  onDelete?: (item: E) => void;
}

export function EntityCrudActions<E extends Entity>({
  dataType,
  item,
  addReturnPath,
  editRouteName,
  editQueryParams,
  canDelete,
  canEdit,
  onDelete
}: PropsWithChildren<EntityCrudActionsProps<E>>): ReactElement<
  any,
  any
> | null {
  const idParam = getIdParamName(dataType);
  const editRouteParams = useMemo(
    () => ({
      [idParam]: item.id
    }),
    [idParam, item]
  );

  const location = useLocation();

  const combinedEditQueryParams = useMemo(
    () =>
      addReturnPath
        ? {
            ...editQueryParams,
            returnPath: `${location.pathname}${location.search}`
          }
        : editQueryParams,
    [editQueryParams, addReturnPath, location]
  );

  // To be able to edit we need a route to link to and either no `canEdit` or one that returns true.
  const showEdit =
    !!editRouteName && (!canEdit || getAccessorValue(item, canEdit));
  // To be able to delete we need an `onDelete` and either no `canDelete` or one that returns true.
  const showDelete =
    !!onDelete && (!canDelete || getAccessorValue(item, canDelete));

  return (
    <HStack
      spacing={2}
      whiteSpace="nowrap"
      justifyContent="flex-end"
      data-testid={generateTestId.section("entity-crud-actions", dataType)}
    >
      {showEdit && (
        <EntityEditButton
          as={NamedLink}
          name={editRouteName}
          params={editRouteParams}
          queryParams={combinedEditQueryParams}
          data-testid={generateTestId.button("edit", dataType, item.id)}
        />
      )}
      {showDelete && (
        <EntityDeleteButton
          onConfirm={() => {
            onDelete(item);
          }}
          data-testid={generateTestId.button("delete", dataType, item.id)}
        />
      )}
    </HStack>
  );
}
